import { Button } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';

import styled, { css } from 'styled-components';

import { colors } from '../../styles/colors/';
import { deskTopTextStyles } from '../../styles/text-styles/';

const PRIMARY_COLOR_SHADE = {
  color: 'indigo',
  default: 7,
  hover: 5,
  focused: 5,
  pressed: 8,
  loading: 6,
};

const PRIMARY_TEXT_COLOR_SHADE = {
  color: 'indigo',
  default: 7,
  hover: 2,
  focused: 1,
  pressed: 3,
  loading: 3,
};

const PRIMARY_LINK_COLOR_SHADE = {
  color: 'indigo',
  default: 7,
  hover: 5,
  focused: 5,
  pressed: 8,
  loading: 6,
};

const DANGER_COLOR_SHADE = {
  color: 'red',
  default: 5,
  hover: 4,
  focused: 4,
  pressed: 6,
  loading: 4,
};

const StyledBaseButton = styled(Button)`
  display: flex;
  align-items: center;
  font-size: ${deskTopTextStyles['button']['fontSize']};
  border-radius: 5px !important;
  border: ${(props) =>
    `1px solid ${colors[props.colorandshade['color']][props.colorandshade['default']]}`};
  color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['default']]};
  box-shadow: none;
  background: transparent;

  .ant-btn-loading-icon {
    display: inline-flex;
    vertical-align: middle;
  }

  ${(props) =>
    props.size === 'large' &&
    css`
      padding: 12px 24px;
      height: 48px;
    `}

  ${(props) =>
    props.size === 'default' &&
    css`
      padding: 5px 16px;
      height: 32px;
      font-size: 14px;
    `}

  ${(props) =>
    props.size === 'small' &&
    css`
      padding: 1px 8px;
      height: 24px;
      font-size: 14px;
    `}

  ${(props) =>
    props.type === 'primary' &&
    css`
      color: ${(props) => colors['white']['1']};
      background: ${(props) =>
        colors[props.colorandshade['color']][props.colorandshade['default']]};
      text-shadow: none;
      box-shadow: none;
    `};

  ${(props) =>
    (props.type === 'link' || props.type === 'text') &&
    css`
      border: none;
      color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['default']]};
    `};

  &:hover {
    ${(props) =>
      (props.type === undefined || props.type === '') &&
      css`
        border: ${(props) =>
          `1px solid ${colors[props.colorandshade['color']][props.colorandshade['hover']]}`};
        color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['hover']]};
      `};

    ${(props) =>
      props.type === 'primary' &&
      css`
        color: ${(props) => colors['gray']['1']};
        background: ${(props) =>
          colors[props.colorandshade['color']][props.colorandshade['hover']]};
        text-shadow: none;
        box-shadow: none;
        border: ${(props) =>
          `1px solid ${colors[props.colorandshade['color']][props.colorandshade['default']]}`};
      `};

    ${(props) =>
      props.type === 'text' &&
      css`
        color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['default']]};
        background: ${(props) =>
          colors[props.colorandshade['color']][props.colorandshade['hover']]};
      `};

    ${(props) =>
      props.type === 'link' &&
      css`
        color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['hover']]};
      `};
  }

  &:focus {
    ${(props) =>
      (props.type === undefined || props.type === '') &&
      css`
        border: ${(props) =>
          `1px solid ${colors[props.colorandshade['color']][props.colorandshade['focused']]}`};
        color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['focused']]};
      `};

    ${(props) =>
      props.type === 'primary' &&
      css`
        color: ${(props) => colors['gray']['1']};
        background: ${(props) =>
          colors[props.colorandshade['color']][props.colorandshade['focused']]};
        text-shadow: none;
        box-shadow: none;
        border: ${(props) =>
          `1px solid ${colors[props.colorandshade['color']][props.colorandshade['default']]}`};
      `};

    ${(props) =>
      props.type === 'text' &&
      css`
        color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['default']]};
        background: ${(props) =>
          colors[props.colorandshade['color']][props.colorandshade['focused']]};
      `};

    ${(props) =>
      props.type === 'link' &&
      css`
        color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['focused']]};
      `};
  }

  &:active {
    ${(props) =>
      (props.type === undefined || props.type === '') &&
      css`
        border: ${(props) =>
          `1px solid ${colors[props.colorandshade['color']][props.colorandshade['pressed']]}`};
        color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['pressed']]};
      `};

    ${(props) =>
      props.type === 'primary' &&
      css`
        color: ${(props) => colors['gray']['1']};
        background: ${(props) =>
          colors[props.colorandshade['color']][props.colorandshade['pressed']]};
        text-shadow: none;
        box-shadow: none;
        border: ${(props) =>
          `1px solid ${colors[props.colorandshade['color']][props.colorandshade['default']]}`};
      `};

    ${(props) =>
      props.type === 'text' &&
      css`
        color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['default']]};
        background: ${(props) =>
          colors[props.colorandshade['color']][props.colorandshade['pressed']]};
      `};

    ${(props) =>
      props.type === 'link' &&
      css`
        color: ${(props) => colors[props.colorandshade['color']][props.colorandshade['pressed']]};
      `};
  }
`;

/**
 * Primary UI component for user interaction
 */
const AntdButton = ({ type, loading, disabled, label, size = 'large', danger, ...props }) => {
  let colorAndShade = PRIMARY_COLOR_SHADE;

  if (type === 'text') {
    colorAndShade = PRIMARY_TEXT_COLOR_SHADE;
  }

  if (type === 'link') {
    colorAndShade = PRIMARY_LINK_COLOR_SHADE;
  }

  if (danger) {
    colorAndShade = DANGER_COLOR_SHADE;
  }

  return (
    <StyledBaseButton
      type={type}
      loading={loading}
      disabled={disabled}
      size={size}
      colorandshade={colorAndShade}
      {...props}
    >
      {label}
    </StyledBaseButton>
  );
};

AntdButton.propTypes = {
  /**
   * Button contents
   */
  label: PropTypes.string.isRequired,

  loading: PropTypes.bool,

  disabled: PropTypes.bool,

  danger: PropTypes.bool,

  /**
   * How large should the button be?
   */
  size: PropTypes.oneOf(['small', 'default', 'large']),
};

AntdButton.defaultProps = {
  loading: false,
  size: 'large',
  disabled: false,
  danger: false,
};

export { AntdButton };
